import React, { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  Paper,
  Button,
  Typography,
  TextField,
  Container,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  makeStyles
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useRequest } from "ahooks";
import SubmitButton from "@/components/commons/SubmitButton";
import useStyles from "@/styles/alarmStyle";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { Form, FormItem } from "@/components/Form";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import NamingCreator from "./NamingCreator";
import {
  getNamingInfoApi,
  createNamingApi,
  updateNamingApi
} from "@/apis/apiCollection";
import HasPermission from "@/components/commons/HasPermission";
import { IconAndroid, IconWindows } from "@/components/Icon";

const useSelfStyles = makeStyles(theme => ({
  alert: {
    background: theme.palette.primary.opacity,
    color: theme.palette.primary.main,
    borderRadius: 10,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3)
  }
}));

const numberReg = /^(0|[1-9]\d*)$/;

const Creation = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const classesSelf = useSelfStyles();

  const [initialValues, setInitialValues] = useState({
    name: "",
    osType: "windows",
    delimiter: "_",
    rules: []
  });

  const detailMode = useRouteMatch({
    path: "/setting/device-naming/info/:id"
  });

  const editMode = useRouteMatch({
    path: "/setting/device-naming/edit/:id"
  });

  const match = detailMode || editMode;

  const [namingIndicator, setNamingIndicator] = useState(
    "Device_000001_Merchant_Android"
  );

  const { loading, runAsync } = useRequest(createNamingApi, {
    manual: true
  });

  const { loading: detailLoading, runAsync: getDetail } = useRequest(
    async () => {
      const detail = await getNamingInfoApi({
        namingRuleId: match.params.id
      });
      detail.rules = JSON.parse(detail.rules);
      detail.rules = detail.rules.map(rule => {
        if (rule.type === 2) {
          return {
            type: rule.type,
            value: rule.startValue,
            currentValue: rule.currentValue
          };
        }
        return rule;
      });
      setInitialValues(detail);
      handleValuesChange(null, detail);
    },
    {
      manual: true
    }
  );

  const { loading: updateLoading, runAsync: runUpdateAsync } = useRequest(
    updateNamingApi,
    {
      manual: true
    }
  );

  const handleSubmit = async values => {
    const newValue = {
      ...values,
      rules: JSON.stringify(
        values.rules.map(rule => {
          if (rule.type === 2) {
            const currentValueFromApi = initialValues.rules.find(
              item => item.type === 2
            )?.currentValue;

            return {
              type: rule.type,
              startValue: Number(rule.value),
              currentValue: currentValueFromApi
            };
          }
          return rule;
        })
      )
    };
    if (editMode) {
      newValue.namingRuleId = editMode.params.id;
      await runUpdateAsync(newValue);
    } else {
      await runAsync(newValue);
    }
    history.goBack();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  const handleValuesChange = (_, values) => {
    const demoMap = {
      1: "",
      2: "",
      3: "86:f9:bd:f0:76:fb",
      4: "Google Pixel 3a",
      5: "DESKTOP-JKAC6R",
      6: "SN",
      7: "Merchant",
      8: "fec"
    };
    if (!values.rules?.length) {
      return;
    }
    const namingIndicator = values.rules
      ?.map(rule => rule.value || demoMap[rule.type])
      .join(values.delimiter);

    setNamingIndicator(namingIndicator);
  };

  useEffect(() => {
    if (match) {
      getDetail({ id: match.params.id });
    }
  }, []);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        {detailLoading ? (
          <LoadingIndicator />
        ) : (
          <Form
            disabled={!!detailMode}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <div className={classes.tableTop}>
              <Typography variant="h5">
                {editMode
                  ? t({ defaultMessage: "编辑命名规则" })
                  : detailMode
                  ? t({ defaultMessage: "命名规则详情" })
                  : t({ defaultMessage: "新增命名规则" })}
              </Typography>
              <div className={classes.rightAction}>
                {detailMode ? (
                  <HasPermission permission="device_naming_method_edit">
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        history.replace(
                          `/setting/device-naming/edit/${match.params.id}`
                        );
                      }}
                    >
                      {t({ defaultMessage: "编辑" })}
                    </Button>
                  </HasPermission>
                ) : (
                  <SubmitButton
                    color="primary"
                    variant="contained"
                    loading={loading || updateLoading}
                  >
                    {t({ defaultMessage: "确定" })}
                  </SubmitButton>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  {t({ defaultMessage: "取消" })}
                </Button>
              </div>
            </div>
            <Container maxWidth="md">
              <FormItem
                row
                required
                labelBold
                colon={false}
                label={t({ defaultMessage: "1. 命名规则名称" })}
                name="name"
                rules={{
                  required: true,
                  message: t({ defaultMessage: "命名规则名称不能为空" })
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                  placeholder={t({ defaultMessage: "请填写名称" })}
                />
              </FormItem>
              <FormItem
                row
                labelBold
                colon={false}
                label={t({
                  defaultMessage: "2. 此命名规则要套用的设备系统类型"
                })}
                name="osType"
              >
                <RadioGroup row>
                  <FormControlLabel
                    disabled={!!detailMode}
                    value="windows"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center">
                        Windows
                        <IconWindows size={24} color="textSecondary" />
                      </Box>
                    }
                  />
                  <FormControlLabel
                    disabled={!!detailMode}
                    value="android"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center">
                        Android
                        <IconAndroid size={24} color="textSecondary" />
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormItem>
              <FormItem
                row
                labelBold
                colon={false}
                label={t({
                  defaultMessage:
                    "3. 为注册在此门店的设备建立命名方式（自动填充设备名称）"
                })}
              >
                <Alert
                  severity="info"
                  icon={false}
                  className={classesSelf.alert}
                >
                  {t({ defaultMessage: "示例：" })}
                  {namingIndicator}
                </Alert>
                <FormItem
                  label={t({
                    defaultMessage: "分隔符"
                  })}
                  labelBold
                  name="delimiter"
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={t({ defaultMessage: "请输入" })}
                  />
                </FormItem>
              </FormItem>
              <FormItem
                row
                name="rules"
                rules={[
                  {
                    required: true,
                    message: t({ defaultMessage: "配置规则不能为空" })
                  },
                  {
                    validator(value, rule) {
                      const keys = value.map(item => item.type);
                      const keySets = [...new Set(keys)];
                      if (keys.length !== keySets.length) {
                        throw new Error(rule.message);
                      }
                    },
                    message: t({ defaultMessage: "有重复的配置规则，请调整" })
                  },
                  {
                    validator(value, rule) {
                      for (const item of value) {
                        if (item.type === 1 && item.value.length > 30) {
                          throw new Error(rule.message);
                        }
                      }
                    },
                    message: t({
                      defaultMessage: "自定义文字长度不能超过30"
                    })
                  },
                  {
                    validator(value, rule) {
                      for (const item of value) {
                        if (item.type === 2 && !numberReg.test(item.value)) {
                          throw new Error(rule.message);
                        }
                      }
                    },
                    message: t({
                      defaultMessage: "自然数递增的起始号码只能为正整数"
                    })
                  }
                ]}
              >
                <NamingCreator disabled={!!detailMode} />
              </FormItem>
            </Container>
          </Form>
        )}
      </Paper>
    </div>
  );
};

export default Creation;
